import { supabase } from '../lib/supabase';

export interface TranslationKey {
  id: string;
  key: string;
  project_id: string;
  created_at: string;
}

export interface TranslationString {
  id: string;
  value: string;
  language_code: string;
  key_id: string;
  created_at: string;
}

export interface TranslationData {
  key: TranslationKey;
  strings: TranslationString[];
}

/**
 * Creates a new translation key
 * @param projectId The ID of the project to create the key in
 * @param keyName The name of the key to create
 * @returns A promise that resolves to the created translation key
 */
export const createTranslationKey = async (
  projectId: string,
  keyName: string
): Promise<TranslationKey> => {
  const { data, error } = await supabase
    .from('TranslationKeys')
    .insert([{ project_id: projectId, key: keyName }])
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Fetches a single translation key by its ID
 * @param keyId The ID of the key to fetch
 * @returns A promise that resolves to the translation key
 */
export const fetchTranslationKeyById = async (keyId: string): Promise<TranslationKey | null> => {
  try {
    const { data, error } = await supabase
      .from('TranslationKeys')
      .select('*')
      .eq('id', keyId)
      .single();

    if (error) {
      return null;
    }

    return data;
  } catch (error) {
    return null;
  }
};

/**
 * Fetches all translation keys for a specific project
 * @param projectId The ID of the project to fetch keys for
 * @returns A promise that resolves to an array of translation keys
 */
export const fetchTranslationKeys = async (projectId: string): Promise<TranslationKey[]> => {
  try {
    const { data, error } = await supabase
      .from('TranslationKeys')
      .select('*')
      .eq('project_id', projectId)
      .order('key', { ascending: true });

    if (error) throw error;

    return data || [];
  } catch (error) {
    throw error;
  }
};

/**
 * Returns the count of translation keys for a project (for free-tier limit checks).
 * @param projectId The ID of the project
 * @returns The number of keys in the project
 */
export const getProjectKeyCount = async (projectId: string): Promise<number> => {
  const { count, error } = await supabase
    .from('TranslationKeys')
    .select('*', { count: 'exact', head: true })
    .eq('project_id', projectId);

  if (error) throw error;
  return count ?? 0;
};

/**
 * Fetches all translation strings for a specific key
 * @param keyId The ID of the key to fetch strings for
 * @returns A promise that resolves to an array of translation strings
 */
export const fetchTranslationStrings = async (keyId: string): Promise<TranslationString[]> => {
  try {
    const { data, error } = await supabase
      .from('TranslationStrings')
      .select('*')
      .eq('key_id', keyId);

    if (error) {
      throw error;
    }

    return data || [];
  } catch (error) {
    throw error;
  }
};

/**
 * Stores the keys in local storage for the current user
 * @param userId The ID of the current user
 * @param keys The translation keys to store
 */
export const storeKeysInLocalStorage = (userId: string, keys: TranslationKey[]): void => {
  try {
    localStorage.setItem(`${userId}_translationKeys`, JSON.stringify(keys));
  } catch (error) {
    // console.error('Error storing keys in local storage:', error);
  }
};

/**
 * Retrieves the keys from local storage for the current user
 * @param userId The ID of the current user
 * @returns The translation keys from local storage, or an empty array if none exist
 */
export const getKeysFromLocalStorage = (userId: string): TranslationKey[] => {
  try {
    const storedKeys = localStorage.getItem(`${userId}_translationKeys`);
    return storedKeys ? JSON.parse(storedKeys) : [];
  } catch (error) {
    // console.error('Error retrieving keys from local storage:', error);
    return [];
  }
};

/**
 * Updates a translation key's name
 * @param keyId The ID of the key to update
 * @param newKeyName The new name for the key
 * @returns A promise that resolves to the updated translation key
 */
export const updateTranslationKey = async (keyId: string, newKeyName: string): Promise<TranslationKey> => {
  const { data, error } = await supabase
    .from('TranslationKeys')
    .update({ key: newKeyName })
    .eq('id', keyId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Updates or inserts a translation string
 * @param keyId The ID of the key the string belongs to
 * @param languageCode The language code for the translation
 * @param value The translated string value
 * @returns A promise that resolves to the updated or inserted translation string
 */
export const upsertTranslationString = async (
  keyId: string, 
  languageCode: string, 
  value: string,
  projectId: string
): Promise<TranslationString> => {
  // First try to find an existing translation string
  const { data: existingData } = await supabase
    .from('TranslationStrings')
    .select('*')
    .eq('key_id', keyId)
    .eq('language_code', languageCode)
    .maybeSingle();

  if (existingData) {
    // Only update if the value has changed
    if (existingData.value !== value) {
      // Update existing string
      const { data, error } = await supabase
        .from('TranslationStrings')
        .update({ value })
        .eq('id', existingData.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    }
    
    // Return existing data if no update was needed
    return existingData;
  } else {
    // Insert new string
    const { data, error } = await supabase
      .from('TranslationStrings')
      .insert([{ 
        key_id: keyId, 
        language_code: languageCode, 
        value,
        project_id: projectId 
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  }
};

/**
 * Deletes a translation key and all its associated strings
 * @param keyId The ID of the key to delete
 * @returns A promise that resolves when the key is deleted
 */
export const deleteTranslationKey = async (keyId: string): Promise<void> => {
  try {
    // First delete all associated translation strings
    const { error: stringsError } = await supabase
      .from('TranslationStrings')
      .delete()
      .eq('key_id', keyId);

    if (stringsError) throw stringsError;

    // Then delete the key itself
    const { error: keyError } = await supabase
      .from('TranslationKeys')
      .delete()
      .eq('id', keyId);

    if (keyError) throw keyError;
  } catch (error) {
    throw error;
  }
};

/**
 * Checks if a translation key exists in a project
 * @param projectId The ID of the project to check in
 * @param keyName The name of the key to check for
 * @returns A promise that resolves to a boolean indicating if the key exists
 */
export const checkKeyExists = async (projectId: string, keyName: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('TranslationKeys')
      .select('id')
      .eq('project_id', projectId)
      .eq('key', keyName)
      .maybeSingle();

    if (error) throw error;
    return !!data;
  } catch (error) {
    throw error;
  }
};

/**
 * Imports translations from a JSON file
 * @param projectId The ID of the project to import translations into
 * @param localeData The parsed JSON data containing translations
 * @param options Import options
 * @returns A promise that resolves to an object containing import statistics
 */
export const importTranslations = async (
  projectId: string,
  localeData: Record<string, any>,
  options: {
    languageMapping?: Record<string, string>; // Map file keys to language codes
    overwriteExisting?: boolean; // Whether to overwrite existing translations
    flattenNestedKeys?: boolean; // Whether to flatten nested keys with dot notation
    resetLanguageList?: boolean; // Whether to reset the language list to only include the target language
    onProgress?: (current: number, total: number) => void; // Callback for progress updates
  } = {}
): Promise<{
  addedLanguages: string[];
  addedKeys: number;
  addedStrings: number;
  skippedKeys: number;
  skippedStrings: number;
  errors: string[];
  deletedStrings?: number;
}> => {
  const stats = {
    addedLanguages: [] as string[],
    addedKeys: 0,
    addedStrings: 0,
    skippedKeys: 0,
    skippedStrings: 0,
    errors: [] as string[],
    deletedStrings: 0
  };

  try {
    // Get project to check existing languages
    const { data: project, error: projectError } = await supabase
      .from('Projects')
      .select('languages')
      .eq('id', projectId)
      .single();

    if (projectError) {
      throw projectError;
    }

    const existingLanguages = project?.languages || [];
    const { 
      languageMapping = {}, 
      overwriteExisting = false, 
      flattenNestedKeys = true,
      resetLanguageList = false,
      onProgress
    } = options;

    // Special case: if languageMapping contains '*', use it as the target language for all
    const targetLanguage = languageMapping['*'];
    
    if (!targetLanguage) {
      throw new Error('No target language specified');
    }
    
    // Determine the new language list
    let newLanguages: string[];
    
    if (resetLanguageList) {
      // Reset to only include the target language
      newLanguages = [targetLanguage];
    } else if (!existingLanguages.includes(targetLanguage)) {
      // Add the target language to existing languages
      newLanguages = [...existingLanguages, targetLanguage];
    } else {
      // No changes needed
      newLanguages = existingLanguages;
    }
    
    // Update the project languages if needed
    if (newLanguages.length !== existingLanguages.length || 
        !newLanguages.every(lang => existingLanguages.includes(lang))) {
      const { error: updateError } = await supabase
        .from('Projects')
        .update({ languages: newLanguages })
        .eq('id', projectId);

      if (updateError) {
        throw updateError;
      }
      
      // Track added languages for stats
      if (resetLanguageList) {
        stats.addedLanguages = [targetLanguage];
      } else if (!existingLanguages.includes(targetLanguage)) {
        stats.addedLanguages.push(targetLanguage);
      }
    }

    // If overwriteExisting is true, delete all existing translations for the target language
    if (overwriteExisting) {
      const { data: existingStrings, error: stringsError } = await supabase
        .from('TranslationStrings')
        .select('id')
        .eq('language_code', targetLanguage)
        .eq('project_id', projectId);
      
      if (stringsError) {
        throw stringsError;
      }
      
      if (existingStrings && existingStrings.length > 0) {
        // Delete all strings for this language
        const { error: deleteError } = await supabase
          .from('TranslationStrings')
          .delete()
          .eq('language_code', targetLanguage)
          .eq('project_id', projectId);
        
        if (deleteError) {
          throw deleteError;
        }
        
        stats.deletedStrings = existingStrings.length;
      }
    }

    // Flatten the data if needed
    const flattenedData = flattenNestedKeys
      ? flattenObject(localeData)
      : flattenTopLevelOnly(localeData);

    const entries = Object.entries(flattenedData).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string'
    );
    const totalKeys = entries.length;

    // Batch import: 1) pre-fetch existing keys and strings, 2) batch create keys, 3) batch write strings
    const BATCH_KEY_INSERT = 100;
    const BATCH_STRING_INSERT = 150;
    const BATCH_STRING_UPDATE = 50;

    // 1) Pre-fetch all existing keys for this project
    const { data: existingKeys, error: keysFetchError } = await supabase
      .from('TranslationKeys')
      .select('id, key')
      .eq('project_id', projectId);

    if (keysFetchError) throw keysFetchError;

    const keyNameToId = new Map<string, string>(
      (existingKeys || []).map((row) => [row.key, row.id])
    );

    // 2) Pre-fetch all existing strings for this project + language
    const { data: existingStrings, error: stringsFetchError } = await supabase
      .from('TranslationStrings')
      .select('id, key_id, value')
      .eq('project_id', projectId)
      .eq('language_code', targetLanguage);

    if (stringsFetchError) throw stringsFetchError;

    const keyIdToExistingString = new Map<string, { id: string; value: string }>(
      (existingStrings || []).map((row) => [row.key_id, { id: row.id, value: row.value }])
    );

    // 3) Ensure all keys exist: batch-insert missing keys
    const keyNamesToCreate = Array.from(
      new Set(entries.map(([k]) => k).filter((k) => !keyNameToId.has(k)))
    );
    for (let i = 0; i < keyNamesToCreate.length; i += BATCH_KEY_INSERT) {
      const chunk = keyNamesToCreate.slice(i, i + BATCH_KEY_INSERT);
      const rows = chunk.map((key) => ({ project_id: projectId, key }));
      const { data: inserted, error: insertKeyError } = await supabase
        .from('TranslationKeys')
        .insert(rows)
        .select('id, key');

      if (insertKeyError) {
        chunk.forEach((keyName) =>
          stats.errors.push(`Error creating key "${keyName}": ${insertKeyError.message}`)
        );
        continue;
      }
      (inserted || []).forEach((row) => keyNameToId.set(row.key, row.id));
      stats.addedKeys += inserted?.length ?? 0;
    }

    // 4) Build lists: strings to insert vs update
    const toInsert: { key_id: string; language_code: string; value: string; project_id: string }[] = [];
    const toUpdate: { id: string; value: string }[] = [];

    for (const [keyName, value] of entries) {
      const keyId = keyNameToId.get(keyName);
      if (!keyId) continue;

      const existing = keyIdToExistingString.get(keyId);
      if (existing && !overwriteExisting) {
        stats.skippedStrings++;
        continue;
      }
      if (existing) {
        if (existing.value !== value) toUpdate.push({ id: existing.id, value });
      } else {
        toInsert.push({
          key_id: keyId,
          language_code: targetLanguage,
          value,
          project_id: projectId,
        });
      }
    }

    stats.addedStrings = toInsert.length + toUpdate.length;
    stats.skippedKeys = totalKeys - keyNamesToCreate.length;

    // 5) Batch insert new strings
    for (let i = 0; i < toInsert.length; i += BATCH_STRING_INSERT) {
      const chunk = toInsert.slice(i, i + BATCH_STRING_INSERT);
      const { error: insertStrError } = await supabase
        .from('TranslationStrings')
        .insert(chunk);

      if (insertStrError) {
        stats.errors.push(`Batch insert strings: ${insertStrError.message}`);
      }
      const insertDone = Math.min(i + BATCH_STRING_INSERT, toInsert.length);
      if (onProgress) onProgress(insertDone + stats.skippedStrings, totalKeys);
    }

    // 6) Batch update existing strings (parallel chunks)
    for (let i = 0; i < toUpdate.length; i += BATCH_STRING_UPDATE) {
      const chunk = toUpdate.slice(i, i + BATCH_STRING_UPDATE);
      await Promise.all(
        chunk.map((row) =>
          supabase.from('TranslationStrings').update({ value: row.value }).eq('id', row.id)
        )
      );
      const updateDone = Math.min(i + BATCH_STRING_UPDATE, toUpdate.length);
      if (onProgress) onProgress(toInsert.length + updateDone + stats.skippedStrings, totalKeys);
    }

    if (onProgress) onProgress(totalKeys, totalKeys);
    return stats;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Helper function to flatten only the top level of an object
 * This preserves plural forms and other nested structures
 * @param obj The object to flatten
 * @returns A flattened object with dot notation for top-level keys only
 */
const flattenTopLevelOnly = (obj: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = {};
  
  for (const key in obj) {
    // Skip numeric keys that are likely array indices
    if (/^\d+$/.test(key)) {
      continue;
    }
    
    const value = obj[key];
    
    if (typeof value === 'string') {
      // Simple string value
      result[key] = value;
    } else if (Array.isArray(value)) {
      // Handle arrays by using the parent key for string values
      // and flattening objects within the array
      const stringValues: string[] = [];
      
      value.forEach((item: any) => {
        if (typeof item === 'string') {
          stringValues.push(item);
        } else if (typeof item === 'object' && item !== null) {
          // For objects in arrays, flatten them with the parent key
          for (const nestedKey in item) {
            if (typeof item[nestedKey] === 'string') {
              result[`${key}.${nestedKey}`] = item[nestedKey];
            }
          }
        }
      });
      
      if (stringValues.length === 1) {
        result[key] = stringValues[0];
      } else if (stringValues.length > 1) {
        result[key] = stringValues.join(', ');
      }
    } else if (typeof value === 'object' && value !== null) {
      // Check if this is a plural form object
      const isPluralForm = Object.keys(value).some(k => 
        ['zero', 'one', 'two', 'few', 'many', 'other'].includes(k)
      );
      
      if (isPluralForm) {
        // For plural forms, create dot notation keys
        for (const pluralKey in value) {
          if (typeof value[pluralKey] === 'string') {
            result[`${key}.${pluralKey}`] = value[pluralKey];
          }
        }
      } else {
        // For other nested objects, try to extract string values
        for (const nestedKey in value) {
          // Skip numeric keys in nested objects
          if (/^\d+$/.test(nestedKey)) {
            continue;
          }
          
          if (typeof value[nestedKey] === 'string') {
            result[`${key}.${nestedKey}`] = value[nestedKey];
          } else if (typeof value[nestedKey] === 'object' && value[nestedKey] !== null && !Array.isArray(value[nestedKey])) {
            // Handle one more level of nesting
            for (const deepKey in value[nestedKey]) {
              // Skip numeric keys in deeply nested objects
              if (/^\d+$/.test(deepKey)) {
                continue;
              }
              
              if (typeof value[nestedKey][deepKey] === 'string') {
                result[`${key}.${nestedKey}.${deepKey}`] = value[nestedKey][deepKey];
              }
            }
          }
        }
      }
    }
  }
  
  return result;
};

/**
 * Helper function to flatten nested objects into dot notation
 * @param obj The object to flatten
 * @param prefix The prefix to use for keys
 * @returns A flattened object with dot notation keys
 */
const flattenObject = (obj: Record<string, any>, prefix = ''): Record<string, any> => {
  return Object.keys(obj).reduce((acc, k) => {
    // Skip numeric keys that are likely array indices
    if (/^\d+$/.test(k)) {
      return acc;
    }
    
    const pre = prefix.length ? `${prefix}.${k}` : k;
    
    // Check if this is a plural form object
    const isPluralForm = typeof obj[k] === 'object' && 
                         !Array.isArray(obj[k]) && 
                         Object.keys(obj[k]).some(pk => 
                           ['zero', 'one', 'two', 'few', 'many', 'other'].includes(pk)
                         );
    
    if (isPluralForm) {
      // For plural forms, keep the structure but flatten with dot notation
      // e.g., pluralDays.one, pluralDays.other
      Object.keys(obj[k]).forEach(pluralKey => {
        if (typeof obj[k][pluralKey] === 'string') {
          acc[`${pre}.${pluralKey}`] = obj[k][pluralKey];
        }
      });
    } else if (Array.isArray(obj[k])) {
      // Handle arrays by creating keys with array indices
      // But only if they contain string values or objects
      obj[k].forEach((item: any, index: number) => {
        if (typeof item === 'string') {
          // Skip creating numeric keys for array items
          // Instead, use the parent key for all array string values
          // This avoids creating numeric keys like "items.0", "items.1"
          if (!acc[pre]) {
            acc[pre] = item;
          } else if (Array.isArray(acc[pre])) {
            acc[pre].push(item);
          } else {
            // Convert to array if we have multiple values
            acc[pre] = [acc[pre] as string, item];
          }
        } else if (typeof item === 'object' && item !== null) {
          // For objects in arrays, flatten them with the parent key
          // This avoids creating numeric indices in the keys
          Object.assign(acc, flattenObject(item, pre));
        }
      });
    } else if (typeof obj[k] === 'object' && obj[k] !== null) {
      // For other objects, continue flattening
      Object.assign(acc, flattenObject(obj[k], pre));
    } else if (typeof obj[k] === 'string') {
      // For string values, add them directly
      acc[pre] = obj[k];
    }
    
    return acc;
  }, {} as Record<string, any>);
};

/**
 * Deletes all translation keys and strings in a project
 * @param projectId The ID of the project to delete all keys and strings from
 * @returns A promise that resolves to an object containing the number of deleted keys and strings
 */
export const deleteAllTranslations = async (projectId: string): Promise<{ deletedKeys: number; deletedStrings: number }> => {
  try {
    // First get all keys for this project
    const { data: keys, error: keysError } = await supabase
      .from('TranslationKeys')
      .select('id')
      .eq('project_id', projectId);
    
    if (keysError) {
      throw keysError;
    }
    
    if (!keys || keys.length === 0) {
      return { deletedKeys: 0, deletedStrings: 0 };
    }
    
    const keyIds = keys.map(key => key.id);
    const keyCount = keys.length;
    
    // Delete all strings for these keys
    const { error: stringsError } = await supabase
      .from('TranslationStrings')
      .delete()
      .in('key_id', keyIds);
    
    if (stringsError) {
      throw stringsError;
    }
    
    // Delete all keys
    const { error: deleteKeysError } = await supabase
      .from('TranslationKeys')
      .delete()
      .eq('project_id', projectId);
    
    if (deleteKeysError) {
      throw deleteKeysError;
    }
    
    return {
      deletedKeys: keyCount,
      deletedStrings: keyCount // Approximate count since we can't get the exact count
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Export translations for a project, creating separate JSON files for each language
 * @param projectId The project ID
 * @param options Export options
 * @returns An object with the exported data by language code
 */
export const exportTranslations = async (
  projectId: string,
  options: {
    languagesToExport?: string[]; // If provided, only export these languages
    formatOptions?: {
      pretty?: boolean; // Whether to format the JSON with indentation
      flattenStructure?: boolean; // Whether to flatten nested keys
      format?: 'json' | 'strings' | 'xml'; // Export format (JSON, iOS .strings, or XML)
    };
    onProgress?: (current: number, total: number) => void; // Progress callback
  } = {}
): Promise<Record<string, { data: Record<string, any> | string; filename: string }>> => {
  const { languagesToExport, formatOptions, onProgress } = options;
  const pretty = formatOptions?.pretty ?? true;
  const flattenStructure = formatOptions?.flattenStructure ?? false;
  const format = formatOptions?.format ?? 'json';
  
  try {
    // Fetch all translation keys for the project
    const { data: keys, error: keysError } = await supabase
      .from('TranslationKeys')
      .select('*')
      .eq('project_id', projectId);
    
    if (keysError) throw keysError;
    if (!keys || keys.length === 0) {
      return {};
    }
    
    // Fetch all languages for the project
    const { data: project, error: projectError } = await supabase
      .from('Projects')
      .select('*')
      .eq('id', projectId)
      .single();
    
    if (projectError || !project) {
      throw projectError || new Error('Project not found');
    }
    
    const projectLanguages = project.languages || [];
    const languages = languagesToExport || projectLanguages;
    
    // Prepare the export data structure
    const exportData: Record<string, { data: Record<string, any> | string; filename: string }> = {};
    
    // Process each language
    for (const lang of options.languagesToExport || projectLanguages) {
      const language = projectLanguages.find((l: string) => l === lang);
      if (!language) continue;
      
      exportData[lang] = {
        data: {},
        filename: format === 'strings' ? `${lang}.strings` : format === 'xml' ? `${lang}.xml` : `${lang}.json`
      };
    }
    
    // Process all keys and their translations
    const totalKeys = keys.length;
    
    for (let i = 0; i < totalKeys; i++) {
      const key = keys[i];
      const keyId = key.id;
      
      // Fetch translations for this key
      const { data: translations, error: translationsError } = await supabase
        .from('TranslationStrings')
        .select('*')
        .eq('key_id', keyId);
      
      if (translationsError) throw translationsError;
      if (!translations) continue;
      
      // Process each language
      for (const lang of languages) {
        // Find translation for this language
        const translation = translations.find((t: any) => t.language_code === lang);
        
        if (translation) {
          const keyPath = key.key;
          const langData = exportData[lang].data;
          
          if (typeof langData === 'object') {
            // Handle nested keys if not flattening
            if (!flattenStructure && keyPath.includes('.')) {
              const parts = keyPath.split('.');
              let current = langData;
              
              // Create nested structure
              for (let j = 0; j < parts.length - 1; j++) {
                const part = parts[j];
                if (!current[part]) {
                  current[part] = {};
                }
                current = current[part];
              }
              
              // Set the value at the deepest level
              current[parts[parts.length - 1]] = translation.value;
            } else {
              // Use the key as is (flattened)
              langData[keyPath] = translation.value;
            }
          }
        }
      }
      
      // Report progress
      if (onProgress) {
        onProgress(i + 1, totalKeys);
      }
    }
    
    // Format the data based on selected format
    for (const lang of languages) {
      if (format === 'strings') {
        // Convert to iOS .strings format
        const langData = exportData[lang].data;
        if (typeof langData === 'object') {
          const stringsData = Object.entries(langData)
            .map(([key, value]) => {
              // Escape special characters in the value
              const escapedValue = (value as string)
                .replace(/"/g, '\\"')
                .replace(/\n/g, '\\n');
              return `"${key}" = "${escapedValue}";`;
            })
            .join('\n\n');
          exportData[lang].data = stringsData;
        }
      } else if (format === 'xml') {
        // Convert to XML format
        const langData = exportData[lang].data;
        if (typeof langData === 'object') {
          const xmlData = `<?xml version="1.0" encoding="UTF-8"?>
<resources>
${Object.entries(langData)
  .map(([key, value]) => {
    // Escape special characters in the value
    const escapedValue = (value as string)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
    return `  <string name="${key}">${escapedValue}</string>`;
  })
  .join('\n')}
</resources>`;
          exportData[lang].data = xmlData;
          exportData[lang].filename = `${lang}.xml`;
        }
      } else if (pretty) {
        // Convert to pretty JSON string and back to object to maintain formatting when downloaded
        const langData = exportData[lang].data;
        if (typeof langData === 'object') {
          const prettyJson = JSON.stringify(langData, null, 2);
          exportData[lang].data = JSON.parse(prettyJson);
        }
      }
    }
    
    return exportData;
  } catch (error) {
    throw error;
  }
}; 

/**
 * Fetches all translation keys and strings for a project in a single optimized query
 * This eliminates the N+1 query problem by using a single JOIN query
 * @param projectId The ID of the project to fetch data for
 * @returns A promise that resolves to an array of translation data with keys and strings
 */
export const fetchAllTranslationData = async (projectId: string): Promise<TranslationData[]> => {
  try {
    const { data, error } = await supabase
      .from('TranslationKeys')
      .select(`
        id,
        key,
        project_id,
        created_at,
        TranslationStrings (
          id,
          value,
          language_code,
          key_id,
          created_at
        )
      `)
      .eq('project_id', projectId)
      .order('key', { ascending: true });

    if (error) throw error;

    // Transform the data to match the expected format
    return (data || []).map(item => ({
      key: {
        id: item.id,
        key: item.key,
        project_id: item.project_id,
        created_at: item.created_at
      },
      strings: item.TranslationStrings || []
    }));
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches translation data with pagination for better performance on large datasets
 * @param projectId The ID of the project to fetch data for
 * @param page The page number (0-based)
 * @param pageSize The number of items per page
 * @returns A promise that resolves to paginated translation data
 */
export const fetchTranslationDataPaginated = async (
  projectId: string,
  page: number = 0,
  pageSize: number = 100
): Promise<{
  data: TranslationData[];
  total: number;
  hasMore: boolean;
}> => {
  try {
    // First get the total count
    const { count, error: countError } = await supabase
      .from('TranslationKeys')
      .select('*', { count: 'exact', head: true })
      .eq('project_id', projectId);

    if (countError) throw countError;

    // Then fetch the paginated data
    const { data, error } = await supabase
      .from('TranslationKeys')
      .select(`
        id,
        key,
        project_id,
        created_at,
        TranslationStrings (
          id,
          value,
          language_code,
          key_id,
          created_at
        )
      `)
      .eq('project_id', projectId)
      .order('key', { ascending: true })
      .range(page * pageSize, (page + 1) * pageSize - 1);

    if (error) throw error;

    const transformedData = (data || []).map(item => ({
      key: {
        id: item.id,
        key: item.key,
        project_id: item.project_id,
        created_at: item.created_at
      },
      strings: item.TranslationStrings || []
    }));

    return {
      data: transformedData,
      total: count || 0,
      hasMore: (page + 1) * pageSize < (count || 0)
    };
  } catch (error) {
    throw error;
  }
}; 